import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import classNames from 'classnames';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedFraction from 'I18n/components/FormattedFraction';
import UIFlex from 'UIComponents/layout/UIFlex';
import Small from 'UIComponents/elements/Small';
import { STAR_RATING, STAR_RATING_FILLED, STAR_RATING_EMPTY_STATE, PILL_RATING, LabelSizesTemplate } from '../constants/RatingTypes';
import { PillIcon, StarRating, StarRatingFilled, StarRatingEmpty } from './AbstractRatingIcons';
export default function Rating({
  'data-test-id': dataTestId,
  direction = 'row',
  height,
  iconSize = 'xs',
  justify,
  label,
  labelSize = 'small',
  rating,
  showTextRating,
  type = STAR_RATING_FILLED,
  use = 'default'
}) {
  const flexAlignment = direction === 'row' ? 'center' : 'start';
  const renderedLabel = useMemo(() => {
    if (!label) {
      return null;
    }

    const labelClassName = direction === 'row' ? ['m-left-3', 'm-bottom-1'] : ['m-top-1'];

    if (use === 'card') {
      return /*#__PURE__*/_jsx(Small, {
        className: "m-left-2 m-bottom-1",
        use: "help",
        children: `(${label})`
      });
    }

    return /*#__PURE__*/_jsx("span", {
      "data-test-id": `${dataTestId}-label`,
      style: {
        fontSize: LabelSizesTemplate[labelSize]
      },
      className: classNames('rating-label', ...labelClassName),
      children: label
    });
  }, [dataTestId, direction, label, labelSize, use]);
  const renderedTextRating = useMemo(() => {
    return use === 'card' ? /*#__PURE__*/_jsx("span", {
      className: "m-left-2",
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedFraction, {
          numerator: I18n.formatNumber(rating, {
            precision: 2
          }),
          denominator: 5
        })
      })
    }) : /*#__PURE__*/_jsx("span", {
      className: "m-left-2",
      children: /*#__PURE__*/_jsx(FormattedFraction, {
        numerator: I18n.formatNumber(rating, {
          precision: 2
        }),
        denominator: 5
      })
    });
  }, [rating, use]);
  const IconComponent = useMemo(() => {
    switch (type) {
      case PILL_RATING:
        return PillIcon;

      case STAR_RATING:
        return StarRating;

      case STAR_RATING_FILLED:
        return StarRatingFilled;

      case STAR_RATING_EMPTY_STATE:
      default:
        return StarRatingEmpty;
    }
  }, [type]);
  const renderedIcons = useMemo(() => {
    return [...new Array(5)].map((__, i) => {
      const index = i + 1; // Creates the partially filled icon based on the rating

      if (i === Math.floor(rating) && rating % 1 !== 0) {
        return /*#__PURE__*/_jsxs("div", {
          style: {
            position: 'relative',
            lineHeight: '117%'
          },
          children: [/*#__PURE__*/_jsx(IconComponent, {
            disabled: true,
            checked: true,
            index: index,
            isForeground: true,
            percentFilled: rating % 1 * 100,
            size: iconSize
          }), /*#__PURE__*/_jsx(IconComponent, {
            disabled: true,
            checked: false,
            index: index,
            isForeground: false,
            percentFilled: 0,
            size: iconSize
          })]
        }, index);
      } // Uses StarRatingFilled for the marigold colored stars when the IconComponent is StarRating


      if (IconComponent === StarRating && index <= rating) {
        return /*#__PURE__*/_jsx(StarRatingFilled, {
          disabled: true,
          checked: true,
          index: index,
          isForeground: false,
          percentFilled: 0,
          size: iconSize
        }, index);
      }

      return /*#__PURE__*/_jsx(IconComponent, {
        disabled: true,
        checked: index <= rating,
        index: index,
        isForeground: false,
        percentFilled: 0,
        size: iconSize
      }, index);
    });
  }, [IconComponent, iconSize, rating]);
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: flexAlignment,
    direction: direction,
    justify: justify,
    style: {
      height
    },
    children: [/*#__PURE__*/_jsxs("div", {
      className: "display-flex",
      "aria-label": unescapedText('marketplace-ui-reviews.rating.overall'),
      "aria-valuemax": 5,
      "aria-valuemin": 0,
      "aria-valuenow": rating,
      "aria-valuetext": unescapedText('marketplace-ui-reviews.rating.accessibleLabel', {
        rating
      }),
      role: "meter",
      children: [renderedIcons, showTextRating && renderedTextRating]
    }), renderedLabel]
  });
}